import Matter from 'matter-js';
import decomp from 'poly-decomp';
// Explicit CJS entry (see the `pixi-cjs` webpack rule in vue.config.js) — importing the
// bare 'pixi.js' specifier here would still resolve to its ESM build regardless of what
// that rule forces for files *inside* node_modules, leaving this file's own top-level
// import mismatched against the CJS-shaped @pixi/* packages it then pulls in.
import * as PIXI from 'pixi.js/dist/cjs/pixi.js';
import Widget from './Widget';
import './banReaperWidget.scss';

Matter.Common.setDecomp(decomp);
const { Engine, Composite, Bodies, Body } = Matter;

const WALL = 40;
const BALL_R = 46;
const REAPER_SIZE = 120;
const DEFAULT_FADE_IN_MS = 500;
const DEFAULT_FADE_OUT_MS = 500;
const DEFAULT_FADE_OUT_DELAY_MS = 1800;
const CHATTER_FADE_MS = 350;
const DROP_R = 12;
const MAX_QUEUED_BANS = 200; // caps queue/pendingChatters growth during a ban flood
const STEP = 1000 / 60; // Matter.js recommends a step <= 16.667ms
const MAX_STEPS_PER_TICK = 6;
// Reaches past DROP_R (the physics collision radius) — see buildBloodBallTexture — so two
// drops that are merely close, not yet overlapping, still sum enough density between them to
// cross BLOOD_THRESHOLD and bridge into one shape.
const BALL_GRADIENT_RADIUS = DROP_R * 2.2;
const BLOOD_THRESHOLD = 0.4; // lower = more of each drop's falloff crosses it, reading as a thicker pool
const BLOOD_THRESHOLD_SOFTNESS = 0.08;
const BLOOD_COLOR = [0.561, 0.204, 0.212, 1]; // #8f3436 — muted red, settings.bloodColor overrides this (see applySettings)
// Shared by the one blood filter below (see its NOISE_GLSL comment) and the settings-driven
// threshold.
const BLOOD_NOISE_UNIFORMS = {
	uColor: BLOOD_COLOR,
	uThreshold: BLOOD_THRESHOLD,
	uThresholdSoftness: BLOOD_THRESHOLD_SOFTNESS,
	uNoiseScale: 80, // higher = smaller noise cells = finer/grainier texture
	uTime: 0, // driven every frame from _tick — see bloodShade's comment on time, below
};

const AVATAR_COLORS = [
	'#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#1abc9c',
	'#3498db', '#9b59b6', '#e91e63', '#00bcd4', '#ff5722',
];

// Each drop is stamped as a soft radial-gradient sprite (buildBloodBallTexture) rather than a
// hard-edged vector circle, additively (BLEND_MODES.ADD — see _getDrop) onto bloodRenderTexture
// instead of just alpha-filled. Overlapping/nearby stamps' falloffs then sum into a density
// field, and thresholding that field (BLOOD_THRESHOLD, smoothstep'd by BLOOD_THRESHOLD_SOFTNESS
// below) both crisps an isolated drop's edge and bridges two merely-close drops into one
// connected pool — the "goo" effect a blur+threshold pass used to produce, without ever
// blurring anything. One sprite, one texture, one filter.
//
// The result is rendered as a plain masked texture: the noise field (marbling) is shaded
// independently of where the mask's edges fall, then the threshold alpha cuts it out — no
// separate edge treatment. A prior version built this up from a multi-octave fbm run through a
// two-level domain warp; simplified down to a single Perlin (gradient) noise lookup instead —
// no octaves, no warp pass.
const NOISE_GLSL = `
	float hash(vec2 p) {
		p = fract(p * vec2(123.34, 456.21));
		p += dot(p, p + 45.32);
		return fract(p.x * p.y);
	}

	// A pseudo-random unit vector per grid corner, rather than a plain random scalar — what
	// makes this gradient (Perlin) noise instead of value noise. Dotting each corner's gradient
	// against the offset to that corner (then blending the four results) is what gives Perlin
	// noise its organic look even as a single octave, unlike value noise's more visibly
	// blobby/grid-aligned look at the same single octave.
	vec2 gradient(vec2 p) {
		float angle = hash(p) * 6.28318530718;
		return vec2(cos(angle), sin(angle));
	}

	float perlinNoise(vec2 p) {
		vec2 i = floor(p);
		vec2 f = fract(p);
		float a = dot(gradient(i), f);
		float b = dot(gradient(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0));
		float c = dot(gradient(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0));
		float d = dot(gradient(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0));
		vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
		float n = mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
		return n * 0.5 + 0.5; // dot products land roughly in [-1, 1]; remap to [0, 1]
	}

	// time drifts the sample position (see _tick, which feeds uTime a running clock in
	// seconds) so the field keeps flowing instead of being a fixed function of screen
	// position. The mask (shapeAlpha, computed outside this function from the density field)
	// is NOT time-driven — only the shading drifts; the pool's actual silhouette still tracks
	// the physics exactly.
	float bloodShade(vec2 texCoord, float noiseScale, float time) {
		vec2 p = texCoord * noiseScale + vec2(time * 0.6, time * -0.45);
		return clamp(perlinNoise(p), 0.0, 1.0);
	}
`;

// Thresholds the additive density field (see the comment above NOISE_GLSL) into the blood's
// actual shape — smoothstep'd across a small band (uThresholdSoftness) for an anti-aliased
// edge instead of a hard binary cutoff — and shades it with the Perlin noise field, all in one
// pass over one texture.
const BLOOD_THRESHOLD_FRAGMENT_SHADER = `
	varying vec2 vTextureCoord;
	uniform sampler2D uSampler;
	uniform vec4 uColor;
	uniform float uThreshold;
	uniform float uThresholdSoftness;
	uniform float uNoiseScale;
	uniform float uTime;

	${NOISE_GLSL}

	void main(void) {
		float density = texture2D(uSampler, vTextureCoord).a;
		float shapeAlpha = smoothstep(uThreshold - uThresholdSoftness, uThreshold + uThresholdSoftness, density);
		float shade = bloodShade(vTextureCoord, uNoiseScale, uTime);
		vec3 mottled = mix(uColor.rgb * 0.7, uColor.rgb * 1.15, shade);
		gl_FragColor = vec4(mottled * shapeAlpha, shapeAlpha);
	}
`;

function hashCode(str) {
	let h = 0;
	for(let i = 0; i < str.length; i++) h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
	return Math.abs(h);
}

// ColorPicker.vue normalizes its v-model to hex ('#rrggbb' / '#rrggbbaa') the moment it mounts,
// regardless of what format the value started as — so hex is the format this actually needs to
// handle; rgba(...) is accepted too only so an old hand-edited/legacy value still works. A
// [0,1] vec4 for the uColor shader uniform out, or `fallback` for anything blank/unparseable.
function parseColorVec4(str, fallback) {
	const s = (str || '').trim();
	const hex = /^#?([0-9a-f]{6}|[0-9a-f]{8})$/i.exec(s);
	if(hex) {
		const h = hex[1];
		return [
			parseInt(h.slice(0, 2), 16) / 255,
			parseInt(h.slice(2, 4), 16) / 255,
			parseInt(h.slice(4, 6), 16) / 255,
			h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
		];
	}
	const rgba = /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)/.exec(s);
	if(rgba) return [parseFloat(rgba[1]) / 255, parseFloat(rgba[2]) / 255, parseFloat(rgba[3]) / 255, rgba[4] != null ? parseFloat(rgba[4]) : 1];
	return fallback;
}

// Pre-rendered once (not per-frame): the soft radial stamp every drop is drawn as — see the
// comment above NOISE_GLSL for why. Sampled at a few stops along (1-r²)² (a standard metaball
// falloff) rather than createRadialGradient's default 2-stop linear ramp, which thresholds
// into a visibly conical edge instead of a round one.
function buildBloodBallTexture() {
	const size = Math.ceil(BALL_GRADIENT_RADIUS * 2);
	const canvas = document.createElement('canvas');
	canvas.width = size; canvas.height = size;
	const ctx = canvas.getContext('2d');
	const c = size / 2;
	const grad = ctx.createRadialGradient(c, c, 0, c, c, BALL_GRADIENT_RADIUS);
	for(const [t, a] of [[0, 1], [0.25, 0.88], [0.5, 0.56], [0.75, 0.19], [1, 0]]) {
		grad.addColorStop(t, `rgba(255,255,255,${a})`);
	}
	ctx.fillStyle = grad;
	ctx.fillRect(0, 0, size, size);
	return PIXI.Texture.from(canvas);
}

// true area centroid of a polygon (Matter re-centers a body here, so we must match it
// or the mesh's UVs drift off the texture it's supposed to be cut from)
function polyCentroid(pts) {
	let a = 0, cx = 0, cy = 0;
	for(let i = 0; i < pts.length; i++) {
		const p = pts[i], q = pts[(i + 1) % pts.length];
		const cross = p.x * q.y - q.x * p.y;
		a += cross; cx += (p.x + q.x) * cross; cy += (p.y + q.y) * cross;
	}
	a *= 0.5;
	return { x: cx / (6 * a), y: cy / (6 * a) };
}

// one side of a straight cut: the disk intersected with a half-plane (always convex).
function segmentVerts(R, phi, d, side) {
	const beta = Math.acos(Math.max(-1, Math.min(1, d / R)));
	const a0 = side > 0 ? phi - beta : phi + beta;
	const a1 = side > 0 ? phi + beta : phi - beta + 2 * Math.PI;
	const N = 30, v = [];
	for(let i = 0; i <= N; i++) {
		const a = a0 + (a1 - a0) * i / N;
		v.push({ x: R * Math.cos(a), y: R * Math.sin(a) });
	}
	return v;
}

function formatDuration(seconds) {
	seconds = parseInt(seconds) || 0;
	if(seconds <= 0) return null;
	if(seconds < 60) return `${seconds}s timeout`;
	if(seconds < 3600) return `${Math.round(seconds / 60)}m timeout`;
	return `${Math.round(seconds / 3600)}h timeout`;
}

function formatBanCount(n) {
	return n === 1 ? '1 ban' : `${n} bans`;
}

function formatTimeoutCount(n) {
	return n === 1 ? '1 timeout' : `${n} timeouts`;
}

// Where the sprite's actually-visible (trimmed) pixels start/end vertically, in its
// parent's local space — not the padded, untrimmed source frame. `sprite.height`/
// `getLocalBounds()` both report the untrimmed box, so labels anchored off those can end
// up floating far from a frame whose real content only fills part of it.
function trimmedVerticalExtent(sprite) {
	const tex = sprite.texture;
	const orig = tex && tex.orig;
	if(!orig || !orig.height) return { top: 0, bottom: 0 };
	const trim = tex.trim;
	const top = trim ? trim.y : 0;
	const bottom = trim ? trim.y + trim.height : orig.height;
	const anchorOffset = sprite.anchor.y * orig.height;
	return {
		top: (top - anchorOffset) * sprite.scale.y,
		bottom: (bottom - anchorOffset) * sprite.scale.y,
	};
}

export default class BanReaperWidget extends Widget {
	constructor({ container }) {
		super({ container });

		this.app = null;
		this.engine = null;
		this.world = null;
		this.W = 0;
		this.H = 0;
		this.FLOOR_Y = 0;

		this.walls = [];
		this.halves = [];
		this.drops = [];
		this.dropPool = [];

		// Single-slot playback: one avatar, one reaper, ever, plus a FIFO of what's
		// still waiting. A moderator with several queued bans plays them all on the one
		// reaper appearance (see onBanEvent/_advanceQueue) instead of each getting its own.
		this.queue = [];
		this.activeAvatar = null;
		this.activeReaper = null;
		this._startingReaper = false;

		// Resolved {texture,time}[] frame arrays, cached by metaUrl so repeat moderators
		// don't re-fetch/re-parse their spritesheet on every ban.
		this._sheetCache = new Map();
		this._placeholder = null;
		// Fallback (no-avatar-set) chatter textures, memoized by (initials,color) — see
		// _avatarFallbackTexture.
		this._fallbackTextureCache = new Map();

		// Per-moderator totals, keyed like onBanEvent's `key` — kept independent of
		// activeReaper so counts survive that moderator's reaper fading out and back in.
		this._banCounts = new Map();
		this._timeoutCounts = new Map();

		this.MAX_DROPS = 900;
		this.bleedSeconds = 1;

		this._resizeObserver = null;
		this._animFrameId = null;
		this._lastTime = performance.now();
		this._acc = 0;
	}

	// ── Build ────────────────────────────────────────────────────────────────

	_build() {
		this.container.style.position = 'relative';
		this.container.style.overflow = 'hidden';

		this.W = Math.max(1, this.container.clientWidth || window.innerWidth);
		this.H = Math.max(1, this.container.clientHeight || window.innerHeight);
		// +DROP_R rests a resting drop's CENTER (not its underside) at the canvas's bottom
		// edge, so the visible canvas cuts across each bottom-row ball through its widest point
		// — hiding the tapering curved underside that would otherwise show as a row of rounded
		// bumps right at the bottom edge, instead of the pool just flatly reaching the edge.
		this.FLOOR_Y = this.H + DROP_R;

		this.app = new PIXI.Application({
			width: this.W, height: this.H,
			backgroundAlpha: 0,
			antialias: true,
			autoDensity: true,
			resolution: window.devicePixelRatio || 1,
			autoStart: false,
		});
		this.container.appendChild(this.app.view);

		// Explicit resolution: RenderTexture.create doesn't otherwise inherit the
		// renderer's own (devicePixelRatio-driven) resolution, and a mismatch there is
		// exactly what makes an upscaled texture look blocky/pixelated on a hi-DPI display.
		this.bloodRenderTexture = PIXI.RenderTexture.create({ width: this.W, height: this.H, resolution: this.app.renderer.resolution });

		// bloodBallsContainer is never added to the stage — it's rendered into
		// bloodRenderTexture each tick (see _updateBloodTexture). Ball sprites are created
		// lazily in _getDrop and live for that drop body's whole pooled lifetime (see
		// _freeDrop).
		this.bloodBallTexture = buildBloodBallTexture();
		this.bloodBallsContainer = new PIXI.Container();

		this.bloodSprite = new PIXI.Sprite(this.bloodRenderTexture);
		this.bloodThresholdFilter = new PIXI.Filter(undefined, BLOOD_THRESHOLD_FRAGMENT_SHADER, { ...BLOOD_NOISE_UNIFORMS });
		this.bloodSprite.filters = [this.bloodThresholdFilter];

		this.avatarLayer = new PIXI.Container();
		this.halvesLayer = new PIXI.Container();
		this.reaperLayer = new PIXI.Container();
		// Reaper first so the avatar (and its cut halves) always render on top of it.
		this.app.stage.addChild(this.bloodSprite, this.reaperLayer, this.halvesLayer, this.avatarLayer);

		this.engine = Engine.create();
		this.engine.gravity.y = 1;
		this.world = this.engine.world;
		this._buildStatics();

		this._resizeObserver = new ResizeObserver(entries => {
			const { width, height } = entries[0].contentRect;
			this._resize(width, height);
		});
		this._resizeObserver.observe(this.container);

		this._lastTime = performance.now();
		this._animFrameId = requestAnimationFrame(t => this._tick(t));
	}

	_generateCSS() {}

	destroy() {
		if(this._animFrameId != null) { cancelAnimationFrame(this._animFrameId); this._animFrameId = null; }
		if(this._resizeObserver) { this._resizeObserver.disconnect(); this._resizeObserver = null; }
		if(this.engine) { Engine.clear(this.engine); this.engine = null; this.world = null; }
		// Never added to the stage tree (see _build), so app.destroy(children:true) below
		// never reaches it or its pooled ball sprites — clean it up explicitly.
		if(this.bloodBallsContainer) { this.bloodBallsContainer.destroy({ children: true, texture: false, baseTexture: false }); this.bloodBallsContainer = null; }
		// Never destroy textures/baseTextures here — they're cached globally by URL and
		// reused across widget instances (the settings-page preview tears this down and
		// rebuilds it on every settings change); only display objects are ours to clean up.
		if(this.app) { this.app.destroy(true, { children: true, texture: false, baseTexture: false }); this.app = null; }
		super.destroy();
	}

	// ── Settings ─────────────────────────────────────────────────────────────

	applySettings(settings) {
		super.applySettings(settings);
		const s = this.settings || {};

		this.MAX_DROPS = s.maxParticles > 0 ? s.maxParticles : 900;
		while(this.drops.length > this.MAX_DROPS) this._freeDrop(this.drops.shift());

		this.bleedSeconds = s.bleedSeconds != null && s.bleedSeconds !== '' ? parseFloat(s.bleedSeconds) : 1;

		if(this.bloodThresholdFilter) this.bloodThresholdFilter.uniforms.uColor = parseColorVec4(s.bloodColor, BLOOD_COLOR);

		// Live-move/resize whichever avatar/reaper is currently showing so position and
		// size settings are visible in the preview immediately, not just on the next spawn.
		this._applyAvatarPosition();
		this._applyAvatarSize();
		this._applyReaperPosition();
		this._applyReaperSize();
		this._applyIdlePingPong();
	}

	// weight+fallback-size in, a PIXI.TextStyle out — `settings.fontSize` (when set)
	// overrides every text element uniformly rather than each keeping its own size.
	_textStyle(weight, defaultSize, fillColor, outlineColor) {
		const s = this.settings || {};
		const size = parseFloat(s.fontSize) > 0 ? parseFloat(s.fontSize) : defaultSize;
		const spread = parseFloat(s.spreadRadius) || 0;
		const blur = parseFloat(s.blurRadius) || 0;
		return new PIXI.TextStyle({
			fontFamily: s.fontFamily || 'ui-sans-serif, -apple-system, sans-serif',
			fontSize: size,
			fontWeight: weight,
			fill: fillColor,
			align: 'center',
			stroke: outlineColor || 'rgba(0, 0, 0, 1)',
			strokeThickness: spread,
			dropShadow: blur > 0,
			dropShadowBlur: blur,
			dropShadowDistance: 0,
			dropShadowAlpha: 1,
			dropShadowColor: outlineColor || 'rgba(0, 0, 0, 1)',
			// PIXI measures the canvas it draws text onto from the glyphs' own metrics, which
			// doesn't leave room for the stroke/shadow around them — so on a descender (p, q,
			// g, y, j) the stroke gets clipped flush against the canvas's bottom edge. That
			// clipped-off sliver is what then shows up bleeding in at the TOP of the texture
			// (texture sampling wraps/clamps onto the adjacent edge of the same tightly-packed
			// canvas), looking like the text "wraps vertically". `padding` adds a margin on
			// every side before any of that measuring happens, so there's actual blank canvas
			// for the stroke/shadow to occupy instead of running off the edge. +2 beyond the
			// nominal stroke/blur size: canvas stroke rendering anti-aliases a little past its
			// own nominal thickness, so sizing padding to the exact value still clips by a
			// pixel or so.
			padding: Math.ceil(spread + blur) + 2,
		});
	}

	// ── Reaper image resolution ──────────────────────────────────────────────

	_findModerator(bannedBy) {
		if(!bannedBy) return null;
		const mods = this.settings?.moderators || [];
		return mods.find(m => {
			if(m.id && bannedBy.id != null && String(m.id) === String(bannedBy.id)) return true;
			const bName = bannedBy.displayName || bannedBy.username;
			return !!(m.username && bName && m.username.toLowerCase() === String(bName).toLowerCase());
		}) || null;
	}

	async _resolveReaperSheets(bannedBy) {
		const s = this.settings || {};
		const match = this._findModerator(bannedBy);
		const name = bannedBy?.displayName || bannedBy?.username || null;

		const idleImg = (match?.reaperImage?.metaUrl ? match.reaperImage : null) || s.defaultReaperImage;
		const slashImg = (match?.reaperSlashImage?.metaUrl ? match.reaperSlashImage : null) || s.defaultReaperSlashImage || idleImg;
		const exitImg = (match?.reaperExitImage?.metaUrl ? match.reaperExitImage : null) || s.defaultReaperExitImage || null;

		const idle = idleImg?.metaUrl ? await this._loadSheet(idleImg) : this._placeholderSheet();
		const slash = slashImg?.metaUrl ? await this._loadSheet(slashImg) : idle;
		// Falls back to idle (same reference, not just same frames) when unset — _updateReaperState
		// compares exitSheet against idleSheet by identity to decide whether there's actually a
		// distinct exit animation to switch to, versus just leaving idle playing through the fade.
		const exit = exitImg?.metaUrl ? await this._loadSheet(exitImg) : idle;

		return { idle, slash, exit, name };
	}

	// Resolved fresh from `bannedBy` (rather than frozen at spawn time) so editing a
	// moderator's offset/size in settings moves the reaper that's already on screen.
	_reaperGeometry(bannedBy) {
		const s = this.settings || {};
		const mod = this._findModerator(bannedBy);
		// Blank/unset (not 0 — 0 is a meaningful "cut immediately") falls through to the
		// moderator's own value, then the global default, then null (cut at animation end).
		const parseDelay = raw => (raw === '' || raw == null) ? null : parseFloat(raw);
		return {
			reaperSize: parseFloat(mod?.reaperSize) || parseFloat(s.reaperSize) || REAPER_SIZE,
			offsetX: parseFloat(mod?.offsetX) || 0,
			offsetY: parseFloat(mod?.offsetY) || 0,
			slashCutDelay: parseDelay(mod?.slashCutDelay) ?? parseDelay(s.slashCutDelay),
		};
	}

	// Blank/unset (not 0 — 0 is a meaningful "instant") falls through to the given default,
	// matching how slashCutDelay/reaperSize already treat their settings fields.
	_fadeSetting(key, def) {
		const s = this.settings || {};
		const raw = s[key];
		const v = (raw === '' || raw == null) ? NaN : parseFloat(raw);
		return Number.isFinite(v) && v >= 0 ? v : def;
	}

	_loadSheet(imgObj) {
		if(!this._sheetCache.has(imgObj.metaUrl)) this._sheetCache.set(imgObj.metaUrl, this._buildSheet(imgObj));
		return this._sheetCache.get(imgObj.metaUrl);
	}

	async _buildSheet(imgObj) {
		const meta = await fetch(imgObj.metaUrl).then(r => r.json());
		const baseTexture = PIXI.BaseTexture.from(imgObj.atlasUrl);
		const sheet = new PIXI.Spritesheet(baseTexture, meta);
		const textures = await sheet.parse();
		// Every atlas reuses the same generic frame_0000.. names, colliding in PIXI's
		// global TextureCache — evict them since we only use the direct references below.
		for(const name of meta.frameOrder) PIXI.Texture.removeFromCache(textures[name]);
		return meta.frameOrder.map((name, i) => ({ texture: textures[name], time: meta.frameDurations[i] || 100 }));
	}

	// Zero-config fallback so the widget isn't silently blank before anything's uploaded.
	_placeholderSheet() {
		if(this._placeholder) return this._placeholder;
		const size = 128;
		const canvas = document.createElement('canvas');
		canvas.width = size; canvas.height = size;
		const ctx = canvas.getContext('2d');
		ctx.font = `${Math.round(size * 0.7)}px serif`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText('💀', size / 2, size / 2 + size * 0.05);
		this._placeholder = [{ texture: PIXI.Texture.from(canvas), time: 1000 }];
		return this._placeholder;
	}

	// ── Avatar textures ──────────────────────────────────────────────────────

	// Memoized by (initials, color) instead of building a fresh canvas+texture per ban —
	// unbounded per-ban textures would otherwise never get freed (see _destroySessionContainer).
	_avatarFallbackTexture(name) {
		const initials = (name || '?').slice(0, 2).toUpperCase();
		const colorIndex = hashCode(name || '') % AVATAR_COLORS.length;
		const cacheKey = `${initials}|${colorIndex}`;
		if(this._fallbackTextureCache.has(cacheKey)) return this._fallbackTextureCache.get(cacheKey);

		const size = 92;
		const canvas = document.createElement('canvas');
		canvas.width = size; canvas.height = size;
		const ctx = canvas.getContext('2d');
		ctx.fillStyle = AVATAR_COLORS[colorIndex];
		ctx.beginPath(); ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2); ctx.fill();
		ctx.fillStyle = 'white';
		ctx.font = 'bold 36px Arial';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText(initials, size / 2, size / 2 + 2);
		// Built directly (not PIXI.Texture.from) so it skips the global TextureCache.
		const texture = new PIXI.Texture(new PIXI.BaseTexture(canvas));
		this._fallbackTextureCache.set(cacheKey, texture);
		return texture;
	}

	// Resolves once `sprite.texture` actually has pixels to show — slashing before that
	// point cuts an avatar-shaped hole with nothing rendered in it (the image was still
	// loading). Races a timeout so a hung/slow load can't stall the reaper forever.
	_applyAvatarTexture(sprite, url, name) {
		if(!url) { sprite.texture = this._avatarFallbackTexture(name); return Promise.resolve(); }
		const texture = PIXI.Texture.from(url);
		sprite.texture = texture;
		if(texture.baseTexture.valid) return Promise.resolve();
		const loaded = new Promise(resolve => {
			// The sprite can already be destroyed by the time this fires (a newer avatar
			// replaced it before the old image finished loading/erroring) — assigning a
			// texture on a destroyed sprite throws.
			texture.baseTexture.once('error', () => { if(!sprite.destroyed) sprite.texture = this._avatarFallbackTexture(name); resolve(); });
			texture.baseTexture.once('loaded', resolve);
		});
		const timeout = new Promise(resolve => setTimeout(resolve, 3000));
		return Promise.race([loaded, timeout]);
	}

	// ── Layout ───────────────────────────────────────────────────────────────

	_resize(width, height) {
		this.W = Math.max(1, Math.round(width));
		this.H = Math.max(1, Math.round(height));
		this.FLOOR_Y = this.H + DROP_R; // see _build's comment on this offset

		if(this.app) {
			this.app.renderer.resize(this.W, this.H);
			this.bloodRenderTexture.resize(this.W, this.H);
		}

		this._buildStatics();

		this._applyAvatarPosition();
		this._applyReaperPosition();
	}

	_buildStatics() {
		if(this.walls.length) Composite.remove(this.world, this.walls);
		this.walls = [
			Bodies.rectangle(this.W / 2, this.FLOOR_Y + WALL, this.W, WALL * 2, { isStatic: true, friction: 0 }),
			Bodies.rectangle(-WALL, this.H / 2, WALL * 2, this.H * 3, { isStatic: true, friction: 0 }),
			Bodies.rectangle(this.W + WALL, this.H / 2, WALL * 2, this.H * 3, { isStatic: true, friction: 0 }),
		];
		Composite.add(this.world, this.walls);
	}

	// avatarX/avatarY (and reaperX/reaperY below) name the TOP-LEFT corner of the element's
	// nominal box, matching how every other position input on the web works — the box is
	// then centered around that corner using the current size, since sprites/text inside
	// are all built anchored to a center origin.
	_avatarPosition() {
		const s = this.settings || {};
		const avatarR = (parseFloat(s.avatarSize) || BALL_R * 2) / 2;
		return {
			x: (parseFloat(s.avatarX) || 0) + avatarR,
			y: (parseFloat(s.avatarY) || 0) + avatarR,
		};
	}

	_reaperPosition(offsetX, offsetY, reaperSize) {
		const s = this.settings || {};
		const size = reaperSize != null ? reaperSize : (parseFloat(s.reaperSize) || REAPER_SIZE);
		return {
			x: (parseFloat(s.reaperX) || 0) + size / 2 + (offsetX || 0),
			y: (parseFloat(s.reaperY) || 0) + size / 2 + (offsetY || 0),
		};
	}

	_applyAvatarPosition() {
		const p = this._avatarPosition();
		if(this.activeAvatar) {
			this.activeAvatar.container.x = p.x;
			this.activeAvatar.container.y = p.y;
		}
		if(this.activeReaper?.chatterLabel) {
			this.activeReaper.chatterLabel.container.x = p.x;
			this.activeReaper.chatterLabel.container.y = p.y;
		}
	}

	_applyReaperPosition() {
		if(!this.activeReaper) return;
		const { offsetX, offsetY, reaperSize } = this._reaperGeometry(this.activeReaper.bannedBy);
		const p = this._reaperPosition(offsetX, offsetY, reaperSize);
		this.activeReaper.container.x = p.x;
		this.activeReaper.container.y = p.y;
	}

	_applyAvatarSize() {
		const av = this.activeAvatar;
		if(av) {
			const avatarR = (parseFloat(this.settings?.avatarSize) || BALL_R * 2) / 2;
			av.r = avatarR;
			av.sprite.width = av.sprite.height = avatarR * 2;
			av.mask.clear().beginFill(0xffffff).drawCircle(0, 0, avatarR).endFill();
		}
		this._applyChatterLabelSize();
	}

	// The name/type label lives in its own container (see _showAvatar) so it can outlive
	// the avatar sprite past a cut — sized separately for the same reason.
	_applyChatterLabelSize() {
		const cl = this.activeReaper?.chatterLabel;
		if(!cl) return;
		const avatarR = (parseFloat(this.settings?.avatarSize) || BALL_R * 2) / 2;
		cl.usernameText.y = avatarR + 16;
		// Anchored off the username's own rendered height (not a fixed gap from the avatar)
		// so it never overlaps regardless of font size.
		if(cl.labelText) cl.labelText.y = cl.usernameText.y + cl.usernameText.height + 4;
	}

	_applyReaperSize() {
		const m = this.activeReaper;
		if(!m) return;
		const { reaperSize } = this._reaperGeometry(m.bannedBy);
		const origSize = m.sprite.texture?.orig || { width: reaperSize, height: reaperSize };
		m.sprite.scale.set(reaperSize / Math.max(origSize.width, origSize.height));
		// Sampled once here (not inside _layoutReaperText) so re-layouting later — e.g. when
		// the score text changes mid-slash — doesn't jump to whatever frame happens to be
		// playing at that instant; different frames can have different trimmed extents.
		m.textAnchorTop = trimmedVerticalExtent(m.sprite).top;
		this._layoutReaperText(m);
	}

	// Stacked upward from the reaper's head: score directly above it, name above that.
	_layoutReaperText(m) {
		const top = m.textAnchorTop;
		m.scoreText.y = top - 12;
		if(m.nameText) m.nameText.y = m.scoreText.visible ? m.scoreText.y - m.scoreText.height - 4 : top - 12;
	}

	// ── Events / queue ───────────────────────────────────────────────────────

	onBanEvent(msg) {
		if(!this.engine) return;
		const sender = msg.sender || {};
		const name = sender.displayName || sender.username || 'chatter';
		const type = msg.type === 'timeout' ? 'timeout' : 'ban';
		const label = type === 'timeout' ? formatDuration(msg.seconds) : null;
		const bannedBy = msg.bannedBy || null;
		const key = bannedBy ? String(bannedBy.id != null ? bannedBy.id : (bannedBy.displayName || bannedBy.username || 'unknown')) : 'unknown';
		const entry = { name, avatarUrl: sender.avatar, label, type, key, bannedBy };

		// Batch onto the currently-showing reaper if it's the same moderator and it hasn't
		// started exiting yet; once it's lingering/fading, it's committed to leaving, and a
		// new ban instead waits in the queue for a fresh idle→slash cycle once this one's gone.
		if(this.activeReaper && this.activeReaper.key === key && this.activeReaper.state !== 'out' && this.activeReaper.state !== 'lingering') {
			const pending = this.activeReaper.pendingChatters;
			pending.push(entry);
			if(pending.length > MAX_QUEUED_BANS) pending.splice(0, pending.length - MAX_QUEUED_BANS);
			return;
		}
		this.queue.push(entry);
		if(this.queue.length > MAX_QUEUED_BANS) this.queue.splice(0, this.queue.length - MAX_QUEUED_BANS);
		this._advanceQueue();
	}

	_advanceQueue() {
		if(this.activeReaper || this._startingReaper) return;
		if(!this.queue.length) return;
		const entry = this.queue.shift();
		this._startingReaper = true;
		this._startReaperSession(entry).finally(() => { this._startingReaper = false; });
	}

	async _startReaperSession(entry) {
		const { idle, slash, exit, name } = await this._resolveReaperSheets(entry.bannedBy);
		if(!this.app) return; // destroyed while sheets were loading

		// Positioned/sized below via _applyReaperPosition/_applyReaperSize, shared with
		// the live settings-change path so a size/position tweak moves the same reaper
		// that's already on screen instead of only affecting the next one spawned.
		const sprite = new PIXI.AnimatedSprite(idle, false);
		sprite.anchor.set(0.5);

		// Carries over from a previous session of this moderator (see _banCounts/_timeoutCounts).
		const banCount = this._banCounts.get(entry.key) || 0;
		const timeoutCount = this._timeoutCounts.get(entry.key) || 0;
		const initialCount = entry.type === 'timeout' ? timeoutCount : banCount;
		const initialText = initialCount > 0
			? (entry.type === 'timeout' ? formatTimeoutCount(initialCount) : formatBanCount(initialCount))
			: '';

		const scoreText = new PIXI.Text(initialText, this._textStyle('700', 16, this.settings?.reaperKillCountColor || '#d81f24', this.settings?.reaperKillCountOutlineColor));
		scoreText.anchor.set(0.5, 1);
		scoreText.visible = initialCount > 0;

		let nameText = null;
		if(name) {
			nameText = new PIXI.Text(name, this._textStyle('700', 17, this.settings?.reaperNameColor || '#e8e2d4', this.settings?.reaperNameOutlineColor));
			nameText.anchor.set(0.5, 1);
		}

		const container = new PIXI.Container();
		container.alpha = 0;
		container.addChild(sprite, scoreText);
		if(nameText) container.addChild(nameText);
		this.reaperLayer.addChild(container);

		this.activeReaper = {
			key: entry.key, name, bannedBy: entry.bannedBy, container, sprite, scoreText, nameText,
			idleSheet: idle, slashSheet: slash, exitSheet: exit,
			banCount, timeoutCount, opacity: 0, state: 'idle',
			pendingChatters: [], lingerUntil: 0,
			cutAt: null, cutDone: false, chatterLabel: null,
			// Fade is a linear ramp from fadeFrom (opacity at the moment the current fade
			// began) to a target, over fadeStartAt..+duration — reset whenever a fade starts
			// (here, and again when entering 'out') rather than recomputed from state alone,
			// so a fade-out that interrupts a still-incomplete fade-in ramps from wherever
			// opacity actually was instead of snapping.
			fadeStartAt: performance.now(), fadeFrom: 0,
		};
		this._applyReaperPosition();
		this._applyReaperSize();
		this._playIdle(this.activeReaper);

		this._showAvatar(entry);
	}

	// PIXI.Text owns a private, unshared texture that only its own destroy() defaults free —
	// a blanket container.destroy({texture:false}) leaks it. Sprites here share a cached
	// texture (avatar URL, or _fallbackTextureCache) and must keep texture:false.
	_destroySessionContainer(container) {
		for(const child of container.children.slice()) {
			if(child instanceof PIXI.Text) child.destroy();
			else child.destroy({ children: true, texture: false, baseTexture: false });
		}
		container.destroy({ children: false });
	}

	_finishReaperSession(m) {
		this.reaperLayer.removeChild(m.container);
		this._destroySessionContainer(m.container);
		this._clearChatterLabel();
		this.activeReaper = null;
		this._advanceQueue();
	}

	// The lingering name/type label (see _showAvatar) belongs to the reaper session, not
	// to whichever avatar is currently showing — it must survive that avatar's own cut.
	_clearChatterLabel() {
		const cl = this.activeReaper?.chatterLabel;
		if(!cl) return;
		this.avatarLayer.removeChild(cl.container);
		this._destroySessionContainer(cl.container);
		this.activeReaper.chatterLabel = null;
	}

	_showAvatar(entry) {
		if(this.activeAvatar) {
			this.avatarLayer.removeChild(this.activeAvatar.container);
			this._destroySessionContainer(this.activeAvatar.container);
		}
		// A new chatter appearing means the previous one's lingering label has done its job.
		this._clearChatterLabel();

		// Positioned/sized below via _applyAvatarPosition/_applyAvatarSize, shared with
		// the live settings-change path so a size/position tweak moves the same avatar
		// that's already on screen instead of only affecting the next one spawned.
		const container = new PIXI.Container();
		const sprite = new PIXI.Sprite();
		sprite.anchor.set(0.5);
		const textureReady = this._applyAvatarTexture(sprite, entry.avatarUrl, entry.name);

		const mask = new PIXI.Graphics();
		sprite.mask = mask;
		container.addChild(sprite, mask);

		const pos = this._avatarPosition();
		container.x = pos.x; container.y = pos.y;
		container.alpha = 0;
		this.avatarLayer.addChild(container);

		this.activeAvatar = { container, sprite, mask, type: entry.type, r: 0, spawnedAt: performance.now(), textureReady: false };
		this._applyAvatarSize();
		textureReady.then(() => {
			if(this.activeAvatar && this.activeAvatar.sprite === sprite) this.activeAvatar.textureReady = true;
		});

		// Kept in its own container (not the avatar's) so it stays on screen through a cut,
		// clearing only when the next chatter replaces it or the reaper session fades out.
		const labelContainer = new PIXI.Container();
		const usernameText = new PIXI.Text(entry.name, this._textStyle('500', 11, this.settings?.textColor || '#e8e2d4', this.settings?.textOutlineColor));
		usernameText.anchor.set(0.5, 0);
		labelContainer.addChild(usernameText);

		let labelText = null;
		if(entry.label) {
			labelText = new PIXI.Text(entry.label, this._textStyle('700', 11, '#d81f24', null));
			labelText.anchor.set(0.5, 0);
			labelContainer.addChild(labelText);
		}

		labelContainer.x = pos.x; labelContainer.y = pos.y;
		labelContainer.alpha = 0;
		this.avatarLayer.addChild(labelContainer);

		if(this.activeReaper) this.activeReaper.chatterLabel = { container: labelContainer, usernameText, labelText, spawnedAt: performance.now() };
		this._applyChatterLabelSize();
	}

	// Ping-pong bounces the idle animation forward then backward instead of restarting
	// from frame 0 each loop — a cheap way to make a short clip read as having more
	// motion/frames than it actually does. Driven via loop:false + onComplete (which
	// clamps exactly to the boundary frame before firing) rather than the loop:true
	// onLoop hook — onLoop fires mid-wrap with the frame slightly past the boundary, so
	// flipping animationSpeed there overshoots back across it on the very next tick and
	// the visible frame just flickers between the first/last frame, never showing
	// anything in between (verified against AnimatedSprite's own update() math).
	_playIdle(m) {
		m.sprite.textures = m.idleSheet;
		m.sprite.animationSpeed = 1;
		if(this.settings?.idlePingPong && m.idleSheet.length > 1) {
			m.sprite.loop = false;
			m.sprite.onComplete = () => {
				m.sprite.animationSpeed *= -1;
				m.sprite.gotoAndPlay(m.sprite.currentFrame);
			};
		} else {
			m.sprite.loop = true;
			m.sprite.onComplete = null;
		}
		m.sprite.gotoAndPlay(0);
	}

	// Toggling the setting live (settings-page preview) should take effect on the reaper
	// that's already idling, not just the next one spawned. Simplest correct option is to
	// just restart idle playback under the new mode rather than patch live mid-bounce state.
	_applyIdlePingPong() {
		const m = this.activeReaper;
		if(!m || m.state !== 'idle') return;
		this._playIdle(m);
	}

	// Loops for as long as the lingering delay lasts, which is independent of the clip's own
	// length — unlike idle there's no ping-pong option here, it just repeats forward.
	_playExit(m) {
		m.sprite.textures = m.exitSheet;
		m.sprite.loop = true;
		m.sprite.animationSpeed = 1;
		m.sprite.onComplete = null;
		m.sprite.gotoAndPlay(0);
	}

	_triggerSlash(m) {
		m.state = 'slashing';
		m.sprite.textures = m.slashSheet;
		m.sprite.loop = false;
		// The idle animation may have left animationSpeed negative mid ping-pong reverse —
		// the slash always plays forward.
		m.sprite.animationSpeed = 1;
		m.sprite.onComplete = () => this._onSlashAnimationComplete(m);
		m.sprite.gotoAndPlay(0);

		// `slashCutDelay` decouples "when does the cut happen" from "when does the slash
		// animation finish" — it's ms after the slash STARTS (0 = cut immediately); left
		// blank, the cut instead fires at animation end, same as before this setting
		// existed. _onSlashAnimationComplete still cuts as a fallback if the animation
		// ends before a configured delay elapses.
		const { slashCutDelay } = this._reaperGeometry(m.bannedBy);
		m.cutAt = slashCutDelay != null ? performance.now() + Math.max(0, slashCutDelay) : null;
		m.cutDone = false;
	}

	_performCut(m) {
		if(m.cutDone) return;
		m.cutDone = true;
		if(this.activeAvatar) {
			const type = this.activeAvatar.type;
			this._cutAvatar();
			if(type === 'timeout') {
				m.timeoutCount++;
				this._timeoutCounts.set(m.key, m.timeoutCount);
				m.scoreText.text = formatTimeoutCount(m.timeoutCount);
			} else {
				m.banCount++;
				this._banCounts.set(m.key, m.banCount);
				m.scoreText.text = formatBanCount(m.banCount);
			}
			m.scoreText.visible = true;
			this._layoutReaperText(m); // the name shifts up once the score appears
		}
	}

	_onSlashAnimationComplete(m) {
		this._performCut(m);
		m.sprite.onComplete = null;

		if(m.pendingChatters.length) {
			this._playIdle(m);
			this._showAvatar(m.pendingChatters.shift());
			m.state = 'idle';
		} else {
			m.state = 'lingering';
			m.lingerUntil = performance.now() + this._fadeSetting('fadeOutDelayMs', DEFAULT_FADE_OUT_DELAY_MS);
			// The exit pose shows for the whole lingering delay, fully opaque — not just once
			// the fade-out itself starts — so it reads as "the reaper exits, then fades" rather
			// than the animation and the fade racing each other. Falls back to idle (unset exit)
			// exactly as before this delay existed.
			if(m.exitSheet !== m.idleSheet) this._playExit(m);
			else this._playIdle(m);
		}
	}

	_updateReaperState(m, now) {
		if(m.state === 'out') {
			const dur = Math.max(1, this._fadeSetting('fadeOutMs', DEFAULT_FADE_OUT_MS));
			const t = Math.min(1, (now - m.fadeStartAt) / dur);
			m.opacity = m.fadeFrom * (1 - t);
			m.container.alpha = m.opacity;
			if(t >= 1) this._finishReaperSession(m);
			return;
		}

		if(m.opacity < 1) {
			const dur = Math.max(1, this._fadeSetting('fadeInMs', DEFAULT_FADE_IN_MS));
			const t = Math.min(1, (now - m.fadeStartAt) / dur);
			m.opacity = m.fadeFrom + (1 - m.fadeFrom) * t;
			m.container.alpha = m.opacity;
		}

		if(m.state === 'idle') {
			const avatarReady = this.activeAvatar && this.activeAvatar.textureReady && (now - this.activeAvatar.spawnedAt >= CHATTER_FADE_MS);
			if(avatarReady && m.opacity > 0.95) this._triggerSlash(m);
		} else if(m.state === 'slashing') {
			if(m.cutAt != null && now >= m.cutAt) this._performCut(m);
		} else if(m.state === 'lingering') {
			// onBanEvent stops batching onto this session once it's lingering, so
			// pendingChatters can no longer grow here — nothing left to do but wait it out.
			if(now >= m.lingerUntil) {
				m.state = 'out';
				m.fadeStartAt = now;
				m.fadeFrom = m.opacity;
			}
		}
	}

	// ── Cut geometry / blood ─────────────────────────────────────────────────

	_cutAvatar() {
		const av = this.activeAvatar;
		if(!av) return;
		const C = this._avatarPosition();
		const r = av.r;
		const texture = av.sprite.texture;

		this.avatarLayer.removeChild(av.container);
		this._destroySessionContainer(av.container);
		this.activeAvatar = null;

		const phi = Math.random() * Math.PI * 2;
		const d = (Math.random() * 2 - 1) * r * 0.55;
		const nrm = { x: Math.cos(phi), y: Math.sin(phi) };

		for(const side of [+1, -1]) {
			const worldV = segmentVerts(r, phi, d, side).map(p => ({ x: C.x + p.x, y: C.y + p.y }));
			const ctr = polyCentroid(worldV);
			const half = Bodies.fromVertices(ctr.x, ctr.y, [worldV],
				{ restitution: 0.1, friction: 0.4, frictionAir: 0.03, density: 0.0006 }, true);
			if(!half) continue;
			Composite.add(this.world, half);
			Body.setVelocity(half, {
				x: side * nrm.x * 2.6 + (Math.random() - 0.5),
				y: side * nrm.y * 2.6 - 1 - Math.random(),
			});
			Body.setAngularVelocity(half, (Math.random() - 0.5) * 0.1);

			const bodyPos = half.position;
			const center = { x: C.x - bodyPos.x, y: C.y - bodyPos.y };
			const localVerts = half.vertices.map(v => ({ x: v.x - bodyPos.x, y: v.y - bodyPos.y }));

			// UV = vertex position relative to the original circle center, normalized to [0,1] —
			// matches how the intact avatar sprite maps the same texture onto a 2r×2r box.
			const verts = [], uvs = [];
			for(const v of localVerts) {
				verts.push(v.x, v.y);
				uvs.push((v.x - center.x + r) / (2 * r), (v.y - center.y + r) / (2 * r));
			}
			const indices = [];
			for(let i = 1; i < localVerts.length - 1; i++) indices.push(0, i, i + 1);

			const mesh = new PIXI.SimpleMesh(texture, new Float32Array(verts), new Float32Array(uvs), new Uint16Array(indices));
			this.halvesLayer.addChild(mesh);

			this.halves.push({
				body: half, mesh, r, center,
				emit: { x: center.x + d * nrm.x, y: center.y + d * nrm.y },
				nrm: { x: -side * nrm.x, y: -side * nrm.y },
				bornAt: performance.now(),
				bleedStart: performance.now(),
				bleedMs: Math.max(1, this.bleedSeconds * 1000) * (0.8 + Math.random() * 0.4),
			});
		}
	}

	_getDrop(x, y) {
		let d = this.dropPool.pop();
		if(d) {
			Body.setPosition(d, { x, y });
			Body.setAngle(d, 0);
			Body.setVelocity(d, { x: 0, y: 0 });
			Body.setAngularVelocity(d, 0);
			Composite.add(this.world, d);
		} else {
			d = Bodies.circle(x, y, DROP_R, { friction: 0, frictionAir: 0.006, restitution: 0.2, density: 0.004, slop: 0.5 });
			Composite.add(this.world, d);
			// Created once per body and reused for its whole pooled lifetime (see _freeDrop) —
			// only position and visibility change after this.
			const sprite = new PIXI.Sprite(this.bloodBallTexture);
			sprite.anchor.set(0.5);
			sprite.blendMode = PIXI.BLEND_MODES.ADD;
			this.bloodBallsContainer.addChild(sprite);
			d.ballSprite = sprite;
		}
		d.ballSprite.visible = true;
		return d;
	}

	_freeDrop(d) {
		Composite.remove(this.world, d);
		d.ballSprite.visible = false;
		this.dropPool.push(d);
	}

	_emitBlood(now) {
		for(const h of this.halves) {
			const life = (now - h.bleedStart) / h.bleedMs;
			if(life >= 1) continue;
			const flow = 1 - life;
			const b = h.body, c = Math.cos(b.angle), s = Math.sin(b.angle);
			const ex = b.position.x + h.emit.x * c - h.emit.y * s;
			const ey = b.position.y + h.emit.x * s + h.emit.y * c;
			const nx = h.nrm.x * c - h.nrm.y * s, ny = h.nrm.x * s + h.nrm.y * c;
			const n = (Math.random() < flow ? 1 : 0) + (Math.random() < flow * 0.7 ? 1 : 0);
			for(let i = 0; i < n; i++) {
				if(this.drops.length >= this.MAX_DROPS) this._freeDrop(this.drops.shift());
				const d = this._getDrop(ex, ey);
				Body.setVelocity(d, {
					x: nx * 1.6 + (Math.random() - 0.5) * 1.8 + b.velocity.x * 0.3,
					y: ny * 1.6 + Math.random() * 0.8 + 0.8,
				});
				d.spawnedAt = now;
				this.drops.push(d);
			}
		}
	}

	_cullDrops(now) {
		for(let i = this.drops.length - 1; i >= 0; i--) {
			const d = this.drops[i];
			const p = d.position;
			if(now - d.spawnedAt > 10000 || p.y > this.H + 80 || p.x < -80 || p.x > this.W + 80) {
				this._freeDrop(d);
				this.drops.splice(i, 1);
			}
		}
	}

	_cullHalves(now) {
		for(let i = this.halves.length - 1; i >= 0; i--) {
			if(now - this.halves[i].bornAt > 10000) {
				const removed = this.halves[i];
				Composite.remove(this.world, removed.body);
				this.halvesLayer.removeChild(removed.mesh);
				removed.mesh.destroy({ texture: false, baseTexture: false });
				this.halves.splice(i, 1);
			}
		}
	}

	_updateHalves() {
		for(const h of this.halves) {
			h.mesh.x = h.body.position.x;
			h.mesh.y = h.body.position.y;
			h.mesh.rotation = h.body.angle;
		}
	}

	// Fills bloodRenderTexture with the additive ball-stamp density field that
	// BLOOD_THRESHOLD_FRAGMENT_SHADER then shapes and shades (see the comment above
	// NOISE_GLSL).
	_updateBloodTexture() {
		for(const d of this.drops) {
			d.ballSprite.x = d.position.x;
			d.ballSprite.y = d.position.y;
		}
		this.app.renderer.render(this.bloodBallsContainer, { renderTexture: this.bloodRenderTexture, clear: true });
	}

	// ── Loop ─────────────────────────────────────────────────────────────────

	_tick(now) {
		const frameMs = Math.min(now - this._lastTime, 100);
		this._acc += frameMs;
		this._lastTime = now;

		if(this.activeAvatar) {
			this.activeAvatar.container.alpha = Math.min(1, (now - this.activeAvatar.spawnedAt) / CHATTER_FADE_MS);
		}
		if(this.activeReaper) {
			this.activeReaper.sprite.update(frameMs / (1000 / 60));
			// _updateReaperState may finish/null this.activeReaper (e.g. once fully faded
			// out) — read chatterLabel off the captured reference, not the field again.
			const m = this.activeReaper;
			this._updateReaperState(m, now);
			// The label fades in on its own once shown, then rides the reaper's own opacity
			// down when the whole session fades out — that's "until the reaper fades out".
			if(m.chatterLabel) {
				const cl = m.chatterLabel;
				const fadeIn = Math.min(1, (now - cl.spawnedAt) / CHATTER_FADE_MS);
				cl.container.alpha = Math.min(fadeIn, m.opacity);
			}
		}

		let steps = 0;
		while(this._acc >= STEP && steps < MAX_STEPS_PER_TICK) {
			this._emitBlood(now);
			Engine.update(this.engine, STEP);
			this._acc -= STEP;
			steps++;
		}

		this._cullDrops(now);
		this._cullHalves(now);
		this._updateHalves();
		this._updateBloodTexture();
		this.bloodThresholdFilter.uniforms.uTime = now / 1000;
		this.app.renderer.render(this.app.stage);

		this._animFrameId = requestAnimationFrame(t => this._tick(t));
	}
}
